// Library imports
import styled from 'styled-components';
import { MDBCol, MDBIcon, MDBListGroup } from 'mdb-react-ui-kit';
// Constant imports
import { POWER_BREAK_POINT } from './constants';
import { MEDIUM_SCREEN_WIDTH } from '../../../../utils/constants';
// Shared styled components
import { CardTitle, HighLightedField, InnerContent } from '../styles';

// Tighter side padding on phones so the three columns get the width
export const PowerContent = styled(InnerContent)`
    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        padding-left: 6px;
        padding-right: 6px;
    }
`;

export const PowerCol = styled(MDBCol)`
    flex: 1 1 0;
    min-width: 0;
`;

// Center data column: compact fixed width on desktop, narrower on small
// devices so the two team columns (flex: 1 1 0) reclaim the space.
export const CenterPowerCol = styled(PowerCol)`
    flex: 0 0 auto;
    max-width: 12rem;

    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        width: 9.5rem;
        max-width: 9.5rem;
    }
`;

export const TeamSectionContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    align-items: stretch;
    height: 100%;
    min-width: 0;
`;

export const TeamNameWrapper = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    max-width: 100%;
    @media (max-width: ${POWER_BREAK_POINT}) {
        align-items: ${p => (p.side === 'left' ? 'flex-start' : 'flex-end')};
    }
    justify-content: flex-start;
`;

// PowerRank-local extension of the shared CardTitle so a long team name
// truncates within its column instead of forcing the column wider.
export const PowerCardTitle = styled(CardTitle)`
    min-width: 0;
    max-width: 100%;

    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        padding: 0.35rem 0;
        font-size: 0.85rem;
    }
`;

export const PowerTeamName = styled.span`
    display: block;
    min-width: 0;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

// Moneyline/Spread switcher: PillNav's button padding is !important, so the
// compact phone sizing has to be overridden from here.
export const PowerPillWrap = styled.div`
    display: flex;
    justify-content: center;
    max-width: 100%;

    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        > div { margin-bottom: 10px; }
        button {
            padding: 0.3rem 0.45rem !important;
            font-size: 0.62rem !important;
            letter-spacing: 0.02em;
            min-height: 28px;
        }
    }
`;

export const PowerField = styled(HighLightedField)`
    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        padding: 5px 6px;
    }
`;

export const TeamLogoWrap = styled.div`
    width: 56px;
    height: 56px;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(255, 255, 255, 0.25);
    box-shadow: 0 0 0 2px rgba(255,255,255,0.12), 0 3px 10px rgba(0, 0, 0, 0.45);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 4px;
    flex-shrink: 0;
    transition: transform 0.2s ease;
    overflow: hidden;

    &:hover {
        transform: scale(1.06);
    }

    @media (max-width: ${POWER_BREAK_POINT}) {
        width: 46px;
        height: 46px;
        margin-bottom: 2px;
    }

    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        width: 38px;
        height: 38px;
    }
`;

export const TeamLogo = styled.img`
    width: 80%;
    height: 80%;
    object-fit: contain;
    object-position: center;
    display: block;
`;

export const PowerTeamId = styled.span`
    display: block;
    font-size: 9px;
    color: #94a3b8;
    margin-top: 0;
    margin-bottom: 6px;
    font-weight: 600;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

export const PowerDataContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
`;

export const PowerItemContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    margin-bottom: 0.75rem;

    @media (max-width: ${MEDIUM_SCREEN_WIDTH}) {
        margin-bottom: 0.5rem;
    }
`;

export const PowerItemLabel = styled.span`
    font-weight: 700;
    color: #94a3b8;
    margin-bottom: 6px;
    font-size: 0.72rem;
    letter-spacing: 0.08em;
    text-transform: uppercase;
`;

export const PowerRankArrow = styled(MDBIcon)`
    color: #475569;
    font-size: 20px;
    border-radius: 50%;
    transition: all 0.2s ease;

    @media (max-width: ${POWER_BREAK_POINT}) {
        font-size: 16px;
        margin: 0px 5px;
    }
`;

export const PowerRankListGroup = styled(MDBListGroup)`
    gap: 3px;
    flex: 1;
    display: flex;
    flex-direction: column;

    .list-group-item {
        flex: 1;
        display: flex !important;
        flex-direction: ${({ direction }) => (direction ? direction : 'column')} !important;
        align-items: ${({ direction }) => (direction === 'row' ? 'center' : 'flex-start')} !important;
        background-color: rgba(255,255,255,0.04) !important;
        border: 1px solid rgba(255,255,255,0.07) !important;
        padding: 5px 9px;
        border-radius: 7px !important;
        min-height: unset;
        transition: all 0.2s ease;

        label,
        span {
            font-size: 0.75rem;
            line-height: 1.3;
            letter-spacing: 0.01em;
        }

        span {
            color: #e2e8f0;
            font-weight: 700;
        }

        label {
            width: auto;
            display: block;
            margin-bottom: 0px;
            font-weight: 500;
            margin-right: auto;
            text-transform: uppercase;
            font-size: 0.62rem;
            letter-spacing: 0.06em;
            color: #94a3b8;
            i {
                margin-right: 4px;
                color: #93c5fd;
                width: 11px;
                text-align: center;
            }
            @media (max-width: ${POWER_BREAK_POINT}) {
                margin-right: ${props => (props.rightJustify ? '0px' : 'auto')} !important;
            }
        }

        @media (max-width: 600px) {
            span { font-size: 0.7rem; }
            label { font-size: 0.6rem; }
            padding: 5px 8px;
        }

        @media (max-width: ${POWER_BREAK_POINT}) {
            flex-direction: ${props => (props.responsive ? 'column' : props.direction ? props.direction : 'column')} !important;
            align-items: ${props =>
                props.rightJustify ? 'flex-end' : props.responsive ? 'flex-start' : props.direction === 'row' ? 'center' : 'flex-start'} !important;
        }

        &.sos-good { border-color: rgba(74,222,128,0.45) !important; }
        &.sos-bad  { border-color: rgba(248,113,113,0.45) !important; }
        &.sos-good span { color: #4ade80; }
        &.sos-bad  span { color: #f87171; }
    }
`;
